import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef
} from "react";
import { doc, onSnapshot } from "firebase/firestore";

import * as Sentry from "@sentry/react";
import {
  Case,
  USCISReceipt,
  CaseStatus,
  UpdateCaseRequest
} from "../api/types/case";
import { CasesAPI } from "../api/cases";
import { VISAVALUE } from "../types/visa.type";
import { db } from "../api/firebaseApi";
import { DATABASE } from "../types/tables-data";

interface CaseContextType {
  case: Case | null;
  isLoading: boolean;
  error: Error | null;
  refetchCase: () => Promise<void>;
  isPetitioner: boolean;
  uscisReceipts: USCISReceipt[];
  isLoadingReceipts: boolean;
  receiptsError: Error | null;
  refetchReceipts: () => Promise<void>;
  /** The tracked receipt, with its first status fetch (or the error from it). */
  addUSCISReceipt: (receiptNumber: string) => Promise<USCISReceipt | null>;
  deleteUSCISReceipt: (receiptId: string) => Promise<void>;
  updateCase: (data: UpdateCaseRequest) => Promise<void>;
  // Applicant Info Autofill status
  isApplicantInfoAutofillProcessing: boolean;
  setApplicantInfoAutofillProcessing: (isProcessing: boolean) => void;
}

const CaseContext = createContext<CaseContextType | undefined>(undefined);

export const useCase = () => {
  const context = useContext(CaseContext);
  if (!context) {
    throw new Error("useCase must be used within a CaseProvider");
  }
  return context;
};

// Useful for components that may render outside a CaseProvider.
export const useCaseOptional = () => {
  return useContext(CaseContext);
};

interface CaseProviderProps {
  individualId: string;
  children: React.ReactNode;
}

export const CaseProvider: React.FC<CaseProviderProps> = ({
  individualId,
  children
}) => {
  const [case_, setCase] = useState<Case | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isPetitioner =
    case_?.visa === VISAVALUE.I601A || case_?.visa === VISAVALUE.I601;
  const [uscisReceipts, setUSCISReceipts] = useState<USCISReceipt[]>([]);
  const [isLoadingReceipts, setIsLoadingReceipts] = useState(true);
  const [receiptsError, setReceiptsError] = useState<Error | null>(null);

  // Applicant Info Autofill status
  const [
    isApplicantInfoAutofillProcessing,
    setIsApplicantInfoAutofillProcessing
  ] = useState(false);

  const unsubscribeRef = useRef<(() => void) | null>(null);

  /**
   * Cleans up the active Firestore listener
   */
  const cleanupCaseListener = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  };

  /**
   * Sets up real-time Firestore listener for case updates
   * @param caseId - The case ID to listen to
   */
  const setupCaseListener = (caseId: string) => {
    // Clean up any existing listener first
    cleanupCaseListener();

    const caseDocRef = doc(db, DATABASE.CASES, caseId);
    unsubscribeRef.current = onSnapshot(
      caseDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const caseData = docSnapshot.data();

          // Update case data with real-time changes
          setCase((prevCase) => {
            if (!prevCase) return null;
            return {
              ...prevCase,
              ...caseData,
              case_id: caseId // Preserve the case_id
            };
          });
        }
      },
      (error) => {
        Sentry.captureException(error);
      }
    );
  };

  const fetchCase = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await CasesAPI.getByIndividualId(individualId);
      setCase(data);

      // Set up real-time listener if we have a case ID
      if (data?.case_id) {
        setupCaseListener(data.case_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch case"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReceipts = async (
    shouldBlockUI = true
  ): Promise<USCISReceipt[] | null> => {
    if (!case_?.case_id) return null;

    try {
      if (shouldBlockUI) {
        setIsLoadingReceipts(true);
      }
      setReceiptsError(null);
      const data = await CasesAPI.getUSCISReceipts(case_.case_id);
      setUSCISReceipts(data);
      return data;
    } catch (err) {
      setReceiptsError(
        err instanceof Error ? err : new Error("Failed to fetch USCIS receipts")
      );
      return null;
    } finally {
      if (shouldBlockUI) {
        setIsLoadingReceipts(false);
      }
    }
  };

  const addUSCISReceipt = async (receiptNumber: string) => {
    if (!case_?.case_id) return null;

    try {
      const created = await CasesAPI.addUSCISReceipt(case_.case_id, {
        uscis_receipt_number: receiptNumber
      });
      const listed = await fetchReceipts(false);
      return (
        listed?.find((r) => r.receipt_id === created.receipt_id) ?? created
      );
    } catch (err) {
      setReceiptsError(
        err instanceof Error ? err : new Error("Failed to add USCIS receipt")
      );
      throw err;
    }
  };

  const deleteUSCISReceipt = async (receiptId: string) => {
    if (!case_?.case_id) return;

    // Optimistic update
    setUSCISReceipts((prev) =>
      prev.filter((receipt) => receipt.receipt_id !== receiptId)
    );

    try {
      await CasesAPI.deleteUSCISReceipt(case_.case_id, receiptId);
    } catch (err) {
      // Revert on error
      setReceiptsError(
        err instanceof Error ? err : new Error("Failed to delete USCIS receipt")
      );
      await fetchReceipts(false);
      throw err;
    }
  };

  const updateCase = async (data: UpdateCaseRequest) => {
    if (!case_?.case_id) return;

    // Optimistic update
    setCase((prev) => (prev ? { ...prev, ...data } : null));

    try {
      await CasesAPI.update(case_.case_id, data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to update case"));
      await fetchCase();
      throw err;
    }
  };

  useEffect(() => {
    fetchCase();

    // Cleanup listener on unmount or individualId change
    return cleanupCaseListener;
  }, [individualId]);

  useEffect(() => {
    if (case_?.case_id) {
      fetchReceipts();
    }
  }, [case_?.case_id]);

  const value = useMemo(
    () => ({
      case: case_,
      isLoading,
      error,
      isPetitioner,
      refetchCase: fetchCase,
      uscisReceipts,
      isLoadingReceipts,
      receiptsError,
      refetchReceipts: async () => {
        await fetchReceipts();
      },
      addUSCISReceipt,
      deleteUSCISReceipt,
      updateCase,
      isApplicantInfoAutofillProcessing,
      setApplicantInfoAutofillProcessing: setIsApplicantInfoAutofillProcessing
    }),
    [
      case_,
      isLoading,
      error,
      isPetitioner,
      uscisReceipts,
      isLoadingReceipts,
      receiptsError,
      isApplicantInfoAutofillProcessing
    ]
  );

  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>;
};
